import type { Dispatch } from 'redux';
import {
  addItemToCart as _addItemToCart,
  applyShippingAddress, fetchShippingRates, placeOrder as _placeOrder
} from 'services/magenum/cart';
import {
  addItemToCartFailure, AddItemToCartRequestData,
  analyticsTrackCheckoutError,
  analyticsTrackCheckoutStepCompleted,
  analyticsTrackFirstPurchase,
  analyticsTrackProductAdded,
  applyDefaultShippingAddressRequest,
  applyPaymentMethod,
  applyShippingMethodFailure,
  applyShippingMethodSuccess,
  fetchCartRequest,
  fetchShippingRatesFailure,
  fetchShippingRatesRequest,
  fetchShippingRatesSuccess,
  placeOrderFailure,
  placeOrderSuccess
} from 'store/cart/actions';
import { Cart__Patched } from '../../pages/checkout/cart';
import QuoteDataShippingMethod from 'mage-swagfaces/quote/QuoteDataShippingMethod';
import { cartCustomerHasAddresses, cartHasItems, cartHasShippingAddress, getProjectIds } from 'helpers/cart';
import CustomerDataAddress from 'mage-swagfaces/customer/CustomerDataAddress';
import { buildShippingMethodPayload } from 'helpers/shipping';
import { getPaymentMethods } from 'services/magenum/customer';
import { fetchCustomerPaymentMethodsFailure, fetchCustomerPaymentMethodsSuccess } from 'store/customer/actions';
import { getGrandTotalFromTotalSegments } from 'store/cart/magentoSelectors';
import { all as allTrue } from 'helpers/conditionals';
import { isNotEmpty } from 'helpers/arrays';
import { newrelic } from 'helpers/reporting/newrelic';
import { FETCH_CUSTOMER_PAYMENT_METHODS_FAILURE } from 'store/customer/constants';
import { optionGet } from 'faunctions';
import { CartPaymentMethod } from 'helpers/billing';
import { postOrderToYotpo } from 'store/cart/vendor/yotpo';
import { clearReturnToProjectDataForProjects } from 'helpers/projects/returnToProject';
import { NEW_CUSTOMER_GROUP, PLACE_ORDER_SUCCESS } from 'store/cart/constants';
import { redirectToPage } from 'helpers/navigation';
import { cartContainsGiftCard, cartPaymentIsGiftcardOrSiteCredit, createPlaceOrderError } from 'store/cart/helpers';
import CustomerDataCustomer from 'mage-swagfaces/customer/CustomerDataCustomer';
import PlaceOrderRequest from 'types-magenum/quote/PlaceOrderRequest';
import QuoteDataTotalSegment from 'mage-swagfaces/quote/QuoteDataTotalSegment';
import SalesDataOrder from 'mage-swagfaces/sales/SalesDataOrder';
import { ProductAddedProperties } from '../../itly';

export const applyShippingMethod = async (
  dispatch: Dispatch,
  address: CustomerDataAddress,
  rate: Partial<QuoteDataShippingMethod>
): Promise<void> => {
  console.log('applyShippingMethod (refactored)'); //remove when the saga is deprecated
  try {
    const response = await applyShippingAddress(buildShippingMethodPayload(address, rate));

    if (response.isRight()) {
      const cart = response.value;
      dispatch(applyShippingMethodSuccess(cart));
      dispatch(fetchCartRequest());
    } else {
      const err = response.value;
      dispatch(applyShippingMethodFailure('Promo code is not valid.'));
      dispatch(analyticsTrackCheckoutError(err, err.body.message, response.cart?.cartId));
    }
  } catch (error) {
    console.error('Error applying shipping method:', error);
    dispatch(applyShippingMethodFailure('An unexpected error occurred.'));
  }
};

export const fetchCustomerPaymentMethods = async (
  dispatch: Dispatch,
  totalSegments: QuoteDataTotalSegment[],
  isPaypalEnabled: boolean,
  isAfterpaySelected: boolean
): Promise<void> => {
  console.log('fetchCustomerPaymentMethods (refactored)'); //remove when the saga is deprecated
  try {
    const response = await getPaymentMethods();

    if (response.isRight()) {
      const paymentMethods = response.value;
      dispatch(fetchCustomerPaymentMethodsSuccess(paymentMethods));

      const isFree = getGrandTotalFromTotalSegments(totalSegments) === 0;

      if (allTrue(!isFree, isNotEmpty(paymentMethods), !isPaypalEnabled, !isAfterpaySelected)) {
        dispatch(applyPaymentMethod(paymentMethods[0]));
      }
    } else {
      const err = response.value;
      newrelic('addPageAction')(FETCH_CUSTOMER_PAYMENT_METHODS_FAILURE, { err });
      dispatch(fetchCustomerPaymentMethodsFailure(optionGet('body.message')(err).getOrElse('Something went wrong.')));
    }
  } catch (error) {
    console.error('Error fetching customer payment methods', error);
    dispatch(fetchCustomerPaymentMethodsFailure(optionGet('body.message')(error).getOrElse('Something went wrong.')));
  }
};

export const getShippingRates = async (dispatch: Dispatch, cart: Cart__Patched): Promise<void> => {
  console.log('getShippingRates (refactored)'); //this can be removed once all sagas are deprecated
  try {
    const response = await fetchShippingRates();

    if (response.isRight()) {
      const shippingRates = response.value;
      dispatch(fetchShippingRatesSuccess(shippingRates));
      const firstRate: QuoteDataShippingMethod | undefined = response.getOrElse([])[0];
      if (cartHasItems(cart) && !cartHasShippingAddress(cart) && cartCustomerHasAddresses(cart) && firstRate) {
        dispatch(applyDefaultShippingAddressRequest(firstRate));
      }
    } else {
      const err = response.value;
      dispatch(fetchShippingRatesFailure(err.body.message));
    }
  } catch (error) {
    console.error('Error getting shipping rates', error);
    dispatch(fetchShippingRatesFailure(error.body.message));
  }
};

export const placeOrder = async (
  dispatch: Dispatch,
  paymentMethod: CartPaymentMethod,
  placeOrderRequest: PlaceOrderRequest,
  cartBeforePurchase: Cart__Patched,
  totalSegments: QuoteDataTotalSegment[],
  customer: CustomerDataCustomer
): Promise<void> => {
  console.log('placeOrder (refactored)');

  if (cartContainsGiftCard(cartBeforePurchase) && cartPaymentIsGiftcardOrSiteCredit(totalSegments)) {
    dispatch(placeOrderFailure(paymentMethod, 'Sorry, you can’t use gift cards or site credit to purchase gift cards'));
    return;
  }
  try {
    const response = await _placeOrder(placeOrderRequest);
    if (response.isRight()) {
      const order: SalesDataOrder = response.value;
      await postOrderToYotpo(order, customer);

      const projectIdsInCart = getProjectIds(cartBeforePurchase);
      await clearReturnToProjectDataForProjects(projectIdsInCart);

      // Report successes to NR, so we can adjust placeOrder PagerDuty alerts off of a percentage
      newrelic('addPageAction')(PLACE_ORDER_SUCCESS);

      if (customer.group_id === NEW_CUSTOMER_GROUP) {
        dispatch(analyticsTrackFirstPurchase(order.subtotal, order.entity_id));
      }

      dispatch(analyticsTrackCheckoutStepCompleted('review', order.quote_id, false, cartBeforePurchase));
      await redirectToPage('/checkout/confirmation', { replace: true });

      dispatch(placeOrderSuccess(order));
    } else {
      const err = response.value;
      createPlaceOrderError(dispatch, err, paymentMethod, cartBeforePurchase.entity_id);
    }
  } catch (error) {
    console.error('Error placing order', error);
  }
};

export const addItemToCart = async (
  dispatch: Dispatch,
  item: AddItemToCartRequestData,
  customerEmail: string,
  analyticsData: ProductAddedProperties
): Promise<void> => {
  try {
    const response = await _addItemToCart(item);

    analyticsData = {
      ...analyticsData,
      ecomm_prodid: item.sku,
      email: customerEmail
    };

    if (response._tag === 'Right') {
      if (response.value.extension_attributes) {
        await redirectToPage('/checkout/cart', { replace: true });

        dispatch(analyticsTrackProductAdded(analyticsData));
        dispatch(fetchCartRequest());
        dispatch(fetchShippingRatesRequest());
      }
    } else {
      dispatch(addItemToCartFailure(response.value.message));
    }
  } catch (err) {
    dispatch(analyticsTrackCheckoutError(err, err.body.message));
  }
};
